import { FULLSCREEN_MODAL_PORTAL_ID } from "../../../useFullscreenModalContainer";

export const GRADING_PANEL_WIDTH = 320;

const PANEL_GAP = 8;
const VIEWPORT_PAD = 12;
const MIN_PANEL_HEIGHT = 160;

export type GradingPanelLayout = { top: number; left: number; height: number | null };

export function measureNaturalPanelHeight(el: HTMLElement): number {
	const s = el.style;
	const prev = { height: s.height, maxHeight: s.maxHeight, overflow: s.overflow };
	s.height = "auto";
	s.maxHeight = "none";
	s.overflow = "visible";
	const h = Math.ceil(el.getBoundingClientRect().height);
	s.height = prev.height;
	s.maxHeight = prev.maxHeight;
	s.overflow = prev.overflow;
	return h;
}

/** Before the first height measurement: anchor under trigger, auto height. */
export function preMeasureGradingPanelLayout(trigger: DOMRectReadOnly): GradingPanelLayout {
	const pad = VIEWPORT_PAD;
	const vw = window.innerWidth;
	const left = Math.min(
		Math.max(pad, trigger.right - GRADING_PANEL_WIDTH),
		Math.max(pad, vw - GRADING_PANEL_WIDTH - pad),
	);
	const top = Math.max(pad, trigger.bottom + PANEL_GAP);
	return { top, left, height: null };
}

/** Content-sized when it fits below the trigger; otherwise min(natural, viewport) with top clamped upward. */
export function computeGradingPanelLayout(trigger: DOMRectReadOnly, natural: number): GradingPanelLayout {
	const vh = window.innerHeight;
	const vw = window.innerWidth;
	const pad = VIEWPORT_PAD;
	const maxViewport = Math.max(MIN_PANEL_HEIGHT, vh - 2 * pad);
	const idealTop = trigger.bottom + PANEL_GAP;
	const spaceBelow = vh - idealTop - pad;
	const left = Math.min(
		Math.max(pad, trigger.right - GRADING_PANEL_WIDTH),
		Math.max(pad, vw - GRADING_PANEL_WIDTH - pad),
	);

	if (natural <= spaceBelow) {
		const height = natural;
		const top = Math.max(pad, Math.min(idealTop, vh - pad - height));
		return { top, left, height };
	}

	const height = Math.min(natural, maxViewport);
	const top = Math.max(pad, Math.min(idealTop, vh - pad - height));
	return { top, left, height };
}

/**
 * Portal target for the grading popover. In fullscreen, document.body is behind the
 * fullscreen layer; use the in-player modal portal instead (see FullscreenModalPortalProvider).
 */
export function gradingPanelPortalRoot(): Element | undefined {
	if (typeof document === "undefined") {
		return undefined;
	}
	if (document.fullscreenElement) {
		return document.getElementById(FULLSCREEN_MODAL_PORTAL_ID) ?? document.body;
	}
	return document.body;
}
